import { useEffect, useRef } from 'react';
import moment from 'moment';
import analyticsService from '@/services/analytics/analytics-service';
import * as Enums from '@/utils/enums';

// Imported on use: storage pulls in helper and, through it, the app's routes, which would make this
// module an import cycle for the components that use it (see posthog-service).
let storage: Promise<any> | null = null;
const loadStorage = () => (storage ??= import('@/utils/storage').then(m => m.default));

/**
 * The onboarding flow's analytics events (sent through analytics-service: PostHog, mirrored to Clarity).
 * Every event carries the shared onboarding properties: the page it happened on, steps completed,
 * demo state, days since the trial started and whether it's a phone (see sharedProperties).
 */

export const ONBOARDING_EVENTS = {
    /** The onboarding dashboard. */
    dashboardViewed: 'onboarding_dashboard_viewed',
    /** A page onboarding sends people to (create job, imports, integrations), with its walkthrough card. */
    walkthroughViewed: 'onboarding_walkthrough_viewed',
    bookDemoClicked: 'onboarding_book_demo_clicked',
    demoBooked: 'demo_booked',
    modalOpened: 'onboarding_modal_opened',
    modalClosed: 'onboarding_modal_closed',
    adminInvited: 'onboarding_admin_invited',
    adminInviteFailed: 'onboarding_admin_invite_failed',
    logoUploaded: 'onboarding_logo_uploaded',
    logoSkipped: 'onboarding_logo_skipped',
    logoUploadFailed: 'onboarding_logo_upload_failed',
    stepCompleted: 'onboarding_step_completed',
    integrationConnected: 'onboarding_integration_connected',
    dataImported: 'onboarding_data_imported',
    connectClicked: 'onboarding_connect_clicked',
    manualImportClicked: 'onboarding_manual_import_clicked',
    backToSetupClicked: 'onboarding_back_to_setup_clicked',
    setupErrorShown: 'onboarding_setup_error_shown',
    graduated: 'onboarding_graduated',
} as const;

export type OnboardingPage = 'dashboard' | 'create_job' | 'imports' | 'integrations';
/** Where a "Book a demo" button sits. */
export type BookDemoLocation = 'manager_card' | 'before_you_start' | 'connect_your_data' | `walkthrough_card_${Exclude<OnboardingPage, 'dashboard'>}`;
export type OnboardingModal = 'before_you_start' | 'connect_your_data' | 'book_demo' | 'invite_admins';
export type DemoState = 'none' | 'booked' | 'held';

interface OnboardingContext {
    page?: OnboardingPage;
    steps_completed?: number;
    demo_state?: DemoState;
}

// What the current screen knows about the company's progress, kept up to date by the dashboard and the
// walkthrough cards so events fired anywhere (modals, buttons) carry it.
let context: OnboardingContext = {};

export const setOnboardingAnalyticsContext = (next: OnboardingContext) => {
    context = { ...context, ...next };
};

const daysSinceTrialStart = (Storage: any): number | null => {
    const first = Storage.getCookie(Enums.Cookie.subscriptionInfo)?.FirstTrialDate;
    const start = first ? moment(first, moment.ISO_8601) : null;
    return start?.isValid() ? moment().startOf('day').diff(start.clone().startOf('day'), 'days') : null;
};

const isPhone = () => typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 48em)').matches;

// Read when the event happens (not once storage has loaded), so it matches that moment.
const sharedProperties = (at: OnboardingContext, Storage: any) => ({
    page: at.page ?? null,
    steps_completed: at.steps_completed ?? null,
    demo_state: at.demo_state ?? null,
    days_since_trial_start: daysSinceTrialStart(Storage),
    is_phone: isPhone(),
});

const send = (at: OnboardingContext, Storage: any, event: string, properties: Record<string, unknown>, setOnce?: Record<string, unknown>) =>
    analyticsService.track(event, { ...sharedProperties(at, Storage), ...properties }, setOnce ? { setOnce } : {});

/** Records an onboarding event with the shared onboarding properties. Never throws. */
export const trackOnboarding = (event: string, properties: Record<string, unknown> = {}, setOnce?: Record<string, unknown>): Promise<void> => {
    const at = { ...context };
    return loadStorage().then(Storage => send(at, Storage, event, properties, setOnce)).catch(() => undefined);
};

const onceKey = (Storage: any) => `sc:onboarding:analytics:${String(Storage.getCookie(Enums.Cookie.tenantID) ?? 'unknown')}`;

const readOnce = (Storage: any): string[] => {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(onceKey(Storage)) ?? '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

/**
 * Tracks an event the first time this browser sees it for the company (a milestone such as a step
 * completing). Resolves to whether it was sent. Another browser or user may send it again; the
 * matching `$set_once` person property keeps the first time.
 */
export const trackOnboardingOnce = (key: string, event: string, properties: Record<string, unknown> = {}, setOnce?: Record<string, unknown>): Promise<boolean> => {
    const at = { ...context };
    return loadStorage().then(Storage => {
        const seen = readOnce(Storage);
        if (seen.includes(key)) return false;
        try {
            window.localStorage.setItem(onceKey(Storage), JSON.stringify([...seen, key]));
        } catch {
            // storage disabled: it may be sent again on a later visit
        }
        send(at, Storage, event, properties, setOnce);
        return true;
    }).catch(() => false);
};

/** How long a page has to stay on screen to count as viewed (see useOnboardingPageView). */
export const PAGE_VIEW_SETTLE_MS = 750;

/**
 * Records one view per visit, once `ready`: `onboarding_dashboard_viewed` for the dashboard and
 * `onboarding_walkthrough_viewed` for the pages with a walkthrough card (`page` says which). The app can mount a page again for a moment
 * while it routes away from it, before the URL changes, so a view only counts if the page is still mounted
 * a moment later and the URL hasn't moved on. This also covers StrictMode running mount effects twice.
 * `onViewed` runs alongside the event (e.g. to tag the Clarity session).
 */
export const useOnboardingPageView = (page: OnboardingPage, ready: boolean, details: Record<string, unknown> = {}, onViewed?: () => void) => {
    const viewed = useRef(false);
    const latest = useRef({ details, onViewed });
    latest.current = { details, onViewed };

    useEffect(() => {
        if (!ready || viewed.current) return;
        const path = window.location.pathname;
        const timer = window.setTimeout(() => {
            if (window.location.pathname !== path) return;
            viewed.current = true;
            trackOnboarding(page === 'dashboard' ? ONBOARDING_EVENTS.dashboardViewed : ONBOARDING_EVENTS.walkthroughViewed, { page, ...latest.current.details });
            latest.current.onViewed?.();
        }, PAGE_VIEW_SETTLE_MS);
        return () => window.clearTimeout(timer);
    }, [ready, page]);
};

/** "Sage One" → "sage_one": package names as event values. */
export const packageSlug = (name: string) => name.trim().toLowerCase().replace(/\s+/g, '_');

/**
 * Tracks a modal opening and closing, with how long it was open and what came of it. Call the returned
 * `setOutcome` before closing it (e.g. 'booked'); a close without one is recorded as 'closed'.
 * `trackOpen: false` skips the open event where another event already records it (Book a demo's click).
 */
export const useOnboardingModalAnalytics = (modal: OnboardingModal, opened: boolean, details: Record<string, unknown> = {}, { trackOpen = true } = {}) => {
    const openedAt = useRef<number | null>(null);
    const outcome = useRef<string | null>(null);
    const detailsRef = useRef(details);
    detailsRef.current = details;

    useEffect(() => {
        if (opened) {
            openedAt.current = Date.now();
            outcome.current = null;
            if (trackOpen) trackOnboarding(ONBOARDING_EVENTS.modalOpened, { modal, ...detailsRef.current });
            return;
        }
        if (openedAt.current === null) return;
        trackOnboarding(ONBOARDING_EVENTS.modalClosed, {
            modal,
            ...detailsRef.current,
            outcome: outcome.current ?? 'closed',
            open_seconds: Math.round((Date.now() - openedAt.current) / 1000),
        });
        openedAt.current = null;
    }, [opened]);

    return (value: string) => { outcome.current = value; };
};
